import {
  ApolloClient,
  DefaultOptions,
  ApolloLink,
  split,
  HttpLink,
  NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { getTokenInfo } from "../auth/tokenUtils";
import { inMemoryCache } from "./inMemoryCache";

export const getClusterSearchParam = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("cluster");
};

export const getMidgardPath = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("midgard_path") || "midgard";
};

// TODO: Add tracing
// import { TRACING_HEADERS } from "../constants/constants";
// import { getShouldAddTracingHeaderToRequests } from "components/Settings/settingsUtils";

/**
 * The `cache-first` fetch policy is used to lessen the load on Askr and Midgard.
 */
export const DEFAULT_OPTIONS: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "cache-first",
    errorPolicy: "none",
  },
  query: {
    fetchPolicy: "cache-first",
    errorPolicy: "none",
  },
};

/**
 * A class that maintains and makes available a map of Apollo GraphQL clients
 * that can connect to a Raven service (e.g., Midgard and Askr) on a
 * particular cluster. Is it useful to maintain a map of clients, rather than
 * a single client, for cases where an application may be using data from
 * multiple tenants.
 *
 * Generally there should be only one instance of ApolloClients per service
 * (e.g., one for Midgard and one for Askr), though this is not enforced.
 */
export class ApolloClients {
  private clients_ = new Map<string, any>();
  private service_: string;

  /**
   * Create a new instance of ApolloClients for a particular service on a
   * particular cluster.
   * @param service The service, e.g., "askr" or "midgard"
   */
  constructor(service: string) {
    this.service_ = service;
  }

  isLocalMidgard(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return Boolean(urlParams.get("local_midgard") === "true" && process.env.REACT_APP_LOCAL);
  }

  /**
   * Get an apollo client for the specific tenant. This will create a new
   * client for each tenant the first time that tenant is requested.
   * @param tenant The tenant code (also known as the ckey or customer code)
   */
  get(tenant: string): ApolloClient<NormalizedCacheObject> {
    const c = this.clients_.get(tenant);
    if (c) {
      return c;
    }

    const defaultCluster = "gke-us-east1.raven-cloud.com";
    const cluster = getClusterSearchParam() ?? defaultCluster;

    const uri =
      this.service_ === "midgard" && this.isLocalMidgard()
        ? "http://localhost:8080/graphql"
        : `https://${tenant}.${cluster}/${this.service_}/graphql/`;

    // https://blog.apollographql.com/batching-client-graphql-queries-a685f5bcd41b
    // link to use if batching (default)
    // also adds a `batch: true` header to the request to prove it's a different link
    const batchHttpLink = new BatchHttpLink({ uri, batchMax: 20 } as BatchHttpLink.Options);
    const batchHttpLinkWithAuth = ApolloLink.from([this.authLink.concat(batchHttpLink)]);

    // link to use if not batching
    const httpLink = new HttpLink({ uri });
    const httpLinkWithAuth = ApolloLink.from([this.authLink.concat(httpLink)]);

    this.clients_.set(
      tenant,
      new ApolloClient({
        cache: inMemoryCache,
        link: split(
          (operation) =>
            // TECHDEBT: investigate batching -- for now, sticking with unbatched
            // if context: {important: true}, use the first link (httpLinkWithAuth), else use the second link (batchHttpLinkWithAuth)
            // operation.getContext().important === true,
            Boolean(operation),
          httpLinkWithAuth,
          batchHttpLinkWithAuth
        ),
        defaultOptions: DEFAULT_OPTIONS,
      })
    );
    // ? Given that we just set this key, can this return undefined?
    return this.clients_.get(tenant);
  }

  authLink = setContext(() => {
    const { idToken } = getTokenInfo();
    // TODO: const shouldAddTracingHeaders = getShouldAddTracingHeaderToRequests();
    // Return the headers to the context so httpLink can read them
    return {
      headers: {
        authorization: idToken ? `Bearer ${idToken}` : "no-token",
        // TODO: ...(shouldAddTracingHeaders ? TRACING_HEADERS : {})
      },
    };
  });
}
